import { Alert, Button, Col, Container, ProgressBar, Row, Spinner, Table } from "react-bootstrap";
import { useState } from "react";
import useMyProperties from "../../Hooks/UseMyProperties/UseMyPropertiesHook";
import { useLazyGetBookingsQuery, useBulkEditPropertiesMutation } from "../../services/bClientApi";
import { PropertyType } from "../../Types/propertyTypes";
import { PropertyBookingEntry } from "../../Types/bookingTypes";
import timeService from "../../services/time/timeService";

// sources whose dateBooked should be used instead of the checkIn date
const DATE_BOOKED_SOURCES: string[] = [];
// const DATE_BOOKED_SOURCES = ['direct', 'website'];

// delay between per-property booking lookups so we don't hammer the API
const QUERY_DELAY_MS = 100;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

interface PreviewEntry {
    propertyId: string;
    title: string;
    currentCreated?: Date;
    newCreated: Date;
    source?: string;
}

export default function QueryWhore() {

    const { myProperties, isLoading: propertiesLoading } = useMyProperties({ allData: true });

    const [triggerGetBookings] = useLazyGetBookingsQuery();
    const [bulkEditProperties, { isLoading: isSaving }] = useBulkEditPropertiesMutation();

    const [previewList, setPreviewList] = useState<PreviewEntry[]>([]);
    const [isScanning, setIsScanning] = useState(false);
    const [hasScanned, setHasScanned] = useState(false);
    const [progress, setProgress] = useState({ current: 0, total: 0 });
    const [applied, setApplied] = useState(false);

    const handleScan = async () => {
        const propertyIds = Object.keys(myProperties);
        setIsScanning(true);
        setHasScanned(false);
        setApplied(false);
        setPreviewList([]);
        setProgress({ current: 0, total: propertyIds.length });

        const entries: PreviewEntry[] = [];

        for (let i = 0; i < propertyIds.length; i++) {
            const propertyId = propertyIds[i];
            const property = myProperties[propertyId] as PropertyType;

            const response = await triggerGetBookings({
                bookingsQuery: { propertyId },
                pagination: { page: 1, limit: 1, sort: { checkIn: 1 } },
            }).unwrap();

            const firstBooking: PropertyBookingEntry | undefined = response?.docs?.[0];

            if (firstBooking) {
                const useDateBooked = DATE_BOOKED_SOURCES.includes(firstBooking.source || '') && !!firstBooking.dateBooked;
                const newCreated = useDateBooked ? firstBooking.dateBooked as Date : firstBooking.checkIn;

                const unchanged = property.created && timeService.sameDay(new Date(property.created), new Date(newCreated));
                if (!unchanged) {
                    entries.push({
                        propertyId,
                        title: property.title,
                        currentCreated: property.created,
                        newCreated,
                        source: firstBooking.source,
                    });
                }
            }

            setProgress({ current: i + 1, total: propertyIds.length });

            if (i < propertyIds.length - 1) {
                await sleep(QUERY_DELAY_MS);
            }
        }

        entries.sort((a, b) => a.title.localeCompare(b.title));
        setPreviewList(entries);
        setIsScanning(false);
        setHasScanned(true);
    };

    const handleApply = async () => {
        const payload = previewList.map((entry) => ({
            _id: entry.propertyId,
            created: entry.newCreated,
        }));
        if (!payload.length) return;
        await bulkEditProperties(payload).unwrap();
        setApplied(true);
    };

    return (
        <Container>
            <Row className="mt-3 mb-2">
                <Col>
                    <h4>Data tool</h4>
                    <p className="text-muted" style={{ fontSize: '0.875rem' }}>
                        Preview and apply corrected "created" dates based on each property's first booking.
                        Bookings sourced from "direct" or "website" use the date booked instead of check-in.
                    </p>
                </Col>
            </Row>

            <Row className="mb-3">
                <Col>
                    <Button
                        variant="secondary"
                        disabled={propertiesLoading || isScanning}
                        onClick={handleScan}
                    >
                        {isScanning ? 'Scanning...' : 'Scan Properties'}
                    </Button>
                </Col>
            </Row>

            {isScanning && (
                <Row className="mb-3">
                    <Col>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Checking property {progress.current} of {progress.total}...
                        <ProgressBar
                            className="mt-2"
                            now={progress.total ? (progress.current / progress.total) * 100 : 0}
                        />
                    </Col>
                </Row>
            )}

            {!isScanning && applied && (
                <Row className="mb-3">
                    <Col>
                        <Alert variant="success">
                            Updated {previewList.length} propert{previewList.length === 1 ? 'y' : 'ies'}.
                        </Alert>
                    </Col>
                </Row>
            )}

            {!isScanning && hasScanned && !applied && (
                <>
                    <Row className="mb-2">
                        <Col>
                            <p>
                                {previewList.length} propert{previewList.length === 1 ? 'y' : 'ies'} will be updated.
                            </p>
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col>
                            <Table hover responsive size="sm">
                                <thead>
                                    <tr>
                                        <th>Property</th>
                                        <th>Current Created</th>
                                        <th>New Created</th>
                                        <th>First Booking Source</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {previewList.map((entry) => (
                                        <tr key={entry.propertyId}>
                                            <td>{entry.title}</td>
                                            <td>{entry.currentCreated ? timeService.getFormattedTime(entry.currentCreated, 'LL/dd/yyyy') : '—'}</td>
                                            <td>{timeService.getFormattedTime(entry.newCreated, 'LL/dd/yyyy')}</td>
                                            <td>{entry.source || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Button
                                variant="primary"
                                disabled={previewList.length === 0 || isSaving}
                                onClick={handleApply}
                            >
                                {isSaving ? 'Updating...' : `Update ${previewList.length} Propert${previewList.length === 1 ? 'y' : 'ies'}`}
                            </Button>
                        </Col>
                    </Row>
                </>
            )}
        </Container>
    );
}